<!--@format-->
<template>
    <div class="document-widget">
        <div class="widget-header">
            <h3>{{ title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <loading-card :is-loading="isLoading" />
        <div v-if="!isLoading" class="content-wrapper">
            <div class="widget-container">
                <starting-step
                    v-if="currentStep === 'startingPoint'"
                    :template-options="startingOptions"
                    :selected-template-option="selectedStartingOption"
                    @input="handleInputStartingOption"
                />
                <existing-template-step
                    v-if="currentStep === 'existingTemplate'"
                    :form="form"
                    @item-preview:click="handleOpenPreview"
                />
                <document-settings-step
                    v-if="currentStep === 'settings'"
                    :is-edit-mode="isEditMode"
                    :form="form"
                    :v="$v"
                    :incomplete="isIncomplete"
                    :invalid-emails="invalidEmails"
                    type="document"
                />
                <document-upload-pdf-step
                    v-if="currentStep === 'uploadFile'"
                    :list-pdf-pages="pdfImagePages"
                    @get-pdf="handlePdf"
                    @get-pdf-collateral="handlePdfCollateral"
                />
            </div>
        </div>
        <div v-if="currentStep !== 'existingTemplate'" class="widget-footer">
            <div class="actions-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="actions-container">
                <app-button
                    v-if="hasBackAction && !isEditMode"
                    class="secondary-btn"
                    @click="goBack"
                >
                    <span class="fa fa-arrow-left"></span>
                    {{ backLabel }}
                </app-button>
                <app-button
                    :disabled="
                        (currentStep === 'settings' || currentStep === 'existingTemplate') &&
                        !isFormValid
                    "
                    @click="goNext"
                >
                    {{ nextLabel }}
                    <span v-if="!isEditFromBuilder" class="fa fa-arrow-right"></span>
                </app-button>
            </div>
        </div>
        <div v-else class="widget-footer existing-template">
            <div class="actions-container">
                <app-button v-if="hasBackAction" class="secondary-btn" @click="goBack">
                    {{ backLabel }}
                </app-button>
                <div class="add-button-container">
                    <div v-if="templatesCount" class="template-count">
                        <span>
                            {{ templatesCount }}
                            {{ templatesCount === 1 ? 'Template' : 'Templates' }} Selected
                        </span>
                    </div>
                    <app-button :disabled="noTemplatesAdded" class="add-btn" @click="goNext">
                        {{ nextLabel }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const StartingStep = () =>
    import('@/components/sliding-widgets/landing-page-template-widget/starting-step.vue');
const DocumentExistingTemplateStep = () =>
    import('@/components/sliding-widgets/document/steps/document-existing-template-step.vue');
const DocumentSettingsStep = () =>
    import('@/components/sliding-widgets/document/steps/document-settings-step.vue');
const DocumentUploadPdfStep = () =>
    import('@/components/sliding-widgets/document/steps/document-upload-pdf-step.vue');
const LoadingCard = () => import('@/components/common/loading-card');

import EventBus from '@/scripts/event-bus';
import BrandingMixin from '@/mixins/branding-mixin';
import DocumentMixin from '@/mixins/document-mixin';
import DocumentBuilderMixin from '@/mixins/builder/document-builder-mixin';
import { DocumentService } from '@/scripts/services/document/document-service';
import { DocumentTemplateService } from '@/scripts/services/document/document-template-service';
import { required } from 'vuelidate/lib/validators';
import { intersectionBy, isNil, isEmpty } from 'lodash';

export default {
    name: 'document-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'starting-step': StartingStep,
        'existing-template-step': DocumentExistingTemplateStep,
        'loading-card': LoadingCard,
        'document-settings-step': DocumentSettingsStep,
        'document-upload-pdf-step': DocumentUploadPdfStep,
    },

    mixins: [BrandingMixin, DocumentMixin, DocumentBuilderMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            selectedStartingOption: null,
            lastActiveStep: null,
            isIncomplete: false,
            invalidEmails: [],

            defaultForm: {
                name: null,
                categories: [],
                recipients: [],
                hasSigningOrder: false,
                deal: {
                    id: null,
                    statusId: null,
                    isLinked: false,
                    isChangingStageOnCompletion: false,
                },

                addedTemplates: [],
                isPublished: false,
                config: '',

                updatedDate: null,
                createdDate: null,
                publishedDate: null,
            },

            form: {
                name: null,
                categories: [],
                recipients: [],
                hasSigningOrder: false,
                deal: {
                    id: null,
                    statusId: null,
                    isLinked: false,
                    isChangingStageOnCompletion: false,
                },

                addedTemplates: [],
                isPublished: false,
                config: '',

                updatedDate: null,
                createdDate: null,
                publishedDate: null,
            },

            pdfImagePages: [],

            steps: {
                startingPoint: {
                    title: 'Create a New Document',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep(this.selectedStartingOption.value);
                            },
                        },
                    },
                },

                existingTemplate: {
                    title: 'Choose templates to add',
                    actions: {
                        nextAction: {
                            label: 'Add',
                            callback: () => {
                                this.injectTemplates();
                            },
                        },

                        backAction: {
                            label: 'Cancel',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                settings: {
                    title: 'Create a New Document',
                    actions: {
                        nextAction: {
                            label: this.widget.options.builder ? 'Save' : 'Next Step',
                            callback: () => {
                                this.isEditMode ? this.patchDocument() : this.createDocument();
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep(this.lastActiveStep);
                            },
                        },
                    },
                },

                uploadFile: {
                    title: 'Create a New Document',
                    actions: {
                        nextAction: {
                            label: 'Next',
                            callback: () => {
                                //this.goToStep('settings');
                                this.injectPagesFromPdf();
                            },
                        },

                        backAction: {
                            label: 'Cancel',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },
            },

            currentStep: 'startingPoint',
        };
    },

    validations: {
        form: {
            name: {
                required,
            },
        },
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        title() {
            return this.isEditMode ? 'Edit Document' : this.steps[this.currentStep].title;
        },

        widgetType() {
            return this.widget.options.type;
        },

        isEditMode() {
            return this.widgetType === 'document' && !isNil(this.widget.options.documentId);
        },

        isEditFromBuilder() {
            return this.widget.options.builder;
        },

        createFromTemplate() {
            return this.widgetType === 'template' && this.widget.options.documentTemplateId;
        },

        isFormValid() {
            return !this.$v.$invalid;
        },

        templatesCount() {
            return this.form.addedTemplates.length;
        },

        noTemplatesAdded() {
            return isEmpty(this.form.addedTemplates);
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        startingOptions() {
            const options = [
                {
                    imageUrl: require('@/assets/images/document-builder-fresh.png'),
                    name: 'Start Fresh',
                    value: 'settings',
                },
                {
                    imageUrl: require('@/assets/images/existing-document-template.png'),
                    name: 'Existing Template',
                    value: 'existingTemplate',
                },
                {
                    imageUrl: require('@/assets/images/upload-pdf.png'),
                    name: 'Upload File',
                    value: 'uploadFile',
                },
            ];

            return options;
        },

        hasBackAction() {
            return (
                this.steps[this.currentStep].actions.backAction !== undefined &&
                !this.isEditMode &&
                !this.createFromTemplate
            );
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        backLabel() {
            return this.steps[this.currentStep].actions.backAction.label;
        },

        nextLabel() {
            return this.steps[this.currentStep].actions.nextAction.label;
        },
    },

    watch: {
        'form.recipients': {
            deep: true,
            handler() {
                this.isIncomplete = false;
                this.invalidEmails = [];
            },
        },
    },

    created() {
        EventBus.$on('opportunity:apply-linking', this.applyDocumentDealChanges);
        if (this.isEditMode) {
            this.goToStep('settings');
            this.getEditDocumentData();
            return;
        }

        if (this.createFromTemplate) {
            this.goToStep('settings');
            this.setDocumentTemplateData(this.widget.options.documentTemplateId);
            return;
        }

        this.isLoading = false;
        this.selectedStartingOption = this.startingOptions[0];
    },

    destroyed() {
        EventBus.$off('opportunity:apply-linking', this.applyDocumentDealChanges);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.lastActiveStep = this.currentStep;
            this.currentStep = step;
        },

        handleInputStartingOption(newOptionSelected) {
            this.selectedStartingOption = newOptionSelected;
        },

        handleOpenPreview({ id }) {
            const route = this.$router.resolve({
                path: `${this.baseUrl}/Document/Template/${id}/Preview`,
            });
            window.open(route.href);
        },

        handlePdf(pdfFile) {
            this.isLoading = true;
            const fd = this.createFormForPdfFile(pdfFile);
            DocumentService.postPdfFormData(fd)
                .then(({ data }) => {
                    let actualLength = this.pdfImagePages.length;
                    data.data.forEach((img, index) => {
                        this.pdfImagePages.push({
                            id: actualLength + (index + 1),
                            img: img,
                            selected: true,
                            order: actualLength + (index + 1),
                        });
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while trying to upload your pdf.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handlePdfCollateral(urlPdf) {
            this.isLoading = true;
            const payload = {
                url: urlPdf,
            };

            DocumentService.postPdfFromCollateral(payload)
                .then(({ data }) => {
                    let actualLength = this.pdfImagePages.length;
                    data.data.forEach((img, index) => {
                        this.pdfImagePages.push({
                            id: actualLength + (index + 1),
                            img: img,
                            selected: true,
                            order: actualLength + (index + 1),
                        });
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while trying to get your pdf.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        cloneConfigPages(config) {
            if (config) {
                const { pages } = JSON.parse(config);
                return pages.map((page) => this.clonePage(page));
            } else {
                return [];
            }
        },

        injectTemplates() {
            let addedPages = [];
            this.isLoading = true;
            const promises = this.form.addedTemplates.map(({ id }) => {
                const config = {
                    select: 'Id,Name,TemplateJson',
                };
                return new Promise((resolve, reject) => {
                    DocumentTemplateService.get(id, config)
                        .then(({ data: { value } }) => {
                            resolve(value[0]);
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
            });

            Promise.all(promises)
                .then((results) => {
                    results.forEach(({ TemplateJson }) => {
                        const clonedPages = this.cloneConfigPages(TemplateJson);
                        addedPages = [...addedPages, ...clonedPages];
                    });
                    this.form.config = JSON.stringify({ pages: [...addedPages], global: {} });
                    this.goToStep('settings');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while adding your templates.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        validateRecipientsInfo() {
            const value = this.form.recipients.every((item) => item.name && item.email);
            this.isIncomplete = !value;
        },

        validateEmails() {
            this.invalidEmails = this.form.recipients.filter(
                ({ email }) => !this.$helpers.isValidEmail(email),
            );
        },

        // Create
        createDocument() {
            this.validateRecipientsInfo();
            if (this.isIncomplete) {
                return;
            }

            this.validateEmails();
            if (this.invalidEmails.length > 0) {
                return;
            }

            this.isLoading = true;
            this.submitDocument()
                .then((Id) => {
                    this.closeWidget();
                    this.$router.push(`${this.baseUrl}/Documents/${Id}/Design`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while creating your document.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                    EventBus.$emit('documents:update');
                });
        },

        submitDocument() {
            return new Promise((resolve, reject) => {
                const documentTemplateId = this.form.addedTemplates.length > 0 ? this.form.addedTemplates[0].id : null;
                
                const mappedCategories = this.form.categories.map((name) => ({
                    Category: name,
                }));

                const mappedRecipients = this.form.recipients
                    ? this.form.recipients.map((recipient) => ({
                          UserId:
                              recipient.type === 'User'
                                  ? recipient.recipientId || recipient.userId
                                  : null,
                          ContactId:
                              recipient.type === 'Contact'
                                  ? recipient.recipientId || recipient.contactId
                                  : null,
                          Name: recipient.name,
                          Email: recipient.email,
                          Color: recipient.color,
                      }))
                    : [];

                const payload = {
                    Name: this.form.name,
                    Categories: mappedCategories,
                    DocumentTemplateId: documentTemplateId,
                    TemplateJson: this.form.config,
                    AccountId: this.accountId,
                    Recipients: mappedRecipients,
                    HasSigningOrder: this.form.hasSigningOrder,
                    /*                     HasBranding: this.form.config
                        ? this.hasDocumentBranding(JSON.parse(this.form.config))
                        : false, */
                };

                if (this.form.deal.isLinked) {
                    if (this.form.deal.id) {
                        payload.DealId = this.form.deal.id;
                    }
                    if (this.form.deal.isChangingStageOnCompletion && this.form.deal.statusId) {
                        payload.DealStatusId = this.form.deal.statusId;
                    }
                }

                if (this.createFromTemplate) {
                    payload.DocumentTemplateId = this.widget.options.documentTemplateId;
                }

                DocumentService.post(payload)
                    .then(({ data: { Id } }) => {
                        this.$helpers.showSuccessNotification('Document created successfully.');
                        resolve(Id);
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        injectPagesFromPdf() {
            const pages = [];
            this.pdfImagePages.forEach((pdfPage) => {
                if (pdfPage.selected) {
                    pages.push(this.createNewFreeFormPage({}, pdfPage.img));
                }
            });

            this.form.config = JSON.stringify({ pages: [...pages], global: {} });
            this.goToStep('settings');
        },

        // Edit
        getEditDocumentData() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;

                const { documentId } = this.widget.options;
                const config = {
                    expand: 'Categories($select=Id,Category),Recipients($expand=User($select=ProfileImageUrl),Contact($select=ProfileImageUrl);$orderby=Id desc)',
                };

                DocumentService.get(documentId, config)
                    .then((response) => {
                        this.setupDocumentInformation(response, true);
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get document info', {
                            error,
                        });
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        setDocumentTemplateData(documentTemplateId) {
            DocumentTemplateService.get(documentTemplateId, {
                select: 'Name,Categories,TemplateJson',
                expand: 'Categories',
            })
                .then((response) => {
                    var template = response.data.value[0];
                    const clonedPages = this.cloneConfigPages(template.TemplateJson);
                    const clonedConfig = JSON.stringify({
                        pages: clonedPages,
                        global: {},
                        totalPages: clonedPages.length,
                    });

                    this.form = {
                        ...this.form,
                        name: template.Name,
                        categories: template.Categories.map(({ Category }) => Category),
                        config: clonedConfig,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your template.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Update
        patchDocument() {
            this.isLoading = true;

            const { documentId } = this.widget.options;

            const mappedCategories = this.form.categories.map((name) => ({
                Category: name,
                DocumentId: documentId,
            }));

            const mappedRecipients = this.form.recipients
                ? this.form.recipients.map((recipient) => ({
                      UserId:
                          recipient.type === 'User'
                              ? recipient.recipientId || recipient.userId
                              : null,
                      ContactId:
                          recipient.type === 'Contact'
                              ? recipient.recipientId || recipient.contactId
                              : null,
                      Name: recipient.name,
                      Email: recipient.email,
                      Color: recipient.color,
                      DocumentId: this.documentId,
                      Id: recipient.new ? 0 : recipient.id,
                      IsSigner: recipient.isSigner,
                  }))
                : [];

            const changes = [
                {
                    name: 'Name',
                    value: this.form.name,
                },
                {
                    name: 'Categories',
                    value: mappedCategories,
                },
                {
                    name: 'TemplateJson',
                    value: this.form.config,
                },
                {
                    name: 'Recipients',
                    value: mappedRecipients,
                },
                {
                    name: 'HasBranding',
                    value: this.form.config
                        ? this.hasDocumentBranding(JSON.parse(this.form.config))
                        : false,
                },
                {
                    name: 'DealId',
                    value: this.form.deal.isLinked ? this.form.deal.id : null,
                },
                {
                    name: 'DealStatusId',
                    value:
                        this.form.deal.isLinked &&
                        this.form.deal.isChangingStageOnCompletion &&
                        this.form.deal.statusId
                            ? this.form.deal.statusId
                            : null,
                },
            ];

            if (this.isEditFromBuilder) {
                changes.splice(2, 1);
            }

            DocumentService.patch(documentId, changes)
                .then(({ data }) => {
                    this.$helpers.showSuccessNotification('Document updated successfully.');

                    if (this.isEditFromBuilder) {
                        EventBus.$emit('document:update-info', data);
                    } else {
                        EventBus.$emit('documents:update');
                        this.$router.push(`${this.baseUrl}/Documents/${documentId}/Design`);
                    }
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to update the document.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        applyDocumentDealChanges({ stageOnCompletion, isChangingStageOnCompletion }) {
            this.form.deal.isChangingStageOnCompletion = isChangingStageOnCompletion;
            if (stageOnCompletion) {
                this.form.deal.statusId = stageOnCompletion.id;
            } else {
                this.form.deal.statusId = null;
            }
        },
    },
};
</script>

<style>
.document-widget.widget .content-wrapper {
    padding: 40px 24px;
}

.document-widget .starting-step .app-input-radio.options {
    gap: 24px;
    justify-content: space-between;
    padding: 30px 45px 30px 45px;
}

.document-widget .starting-step .app-input-radio.horizontal .option-image {
    height: 157px;
    width: 258px;
}

.document-widget .starting-step .app-input-radio .radio-option-label-container .circle::before {
    height: 18px;
    width: 18px;
}

.document-widget .widget-footer .transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.document-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.document-widget .widget-footer .actions-container {
    display: flex;
    justify-content: space-between;
}

.document-widget .widget-footer.existing-template .actions-container {
    width: 100%;
}

.document-widget .widget-footer.existing-template .add-button-container {
    align-items: center;
    display: flex;
    gap: 15px;
}

.document-widget .widget-footer.existing-template .template-count {
    color: #382153;
    font: 500 14px 'Roboto';
}

.document-widget .widget-footer.existing-template .add-btn {
    padding: 0 40px;
}

.document-widget .widget-footer .secondary-btn {
    background: transparent;
    border: 1px solid#18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.document-widget .widget-footer .secondary-btn:hover {
    background: #dbedfa;
}

.document-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.document-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}
</style>
